import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getOrdersApi } from "@/api/take-order/take-order";

// Thunk to fetch pre-orders
export const fetchPreOrders = createAsyncThunk(
  "takeOrder/fetchPreOrders",
  async (
    {
      restaurantId = 1,
      stage = "pre_order",
      page = 1,
      per_page = 4,
      order_type = "created_at",
      search = "",
    } = {},
    thunkAPI
  ) => {
    try {
      const response = await getOrdersApi(restaurantId, stage, page, per_page, order_type, search);

      if (response && response.status === "success" && response.data) {
        const data = response.data;
        const items = data.orders || [];
        const meta = data.pagination || response.pagination || {};

        const pagination = {
          page: meta.current_page || meta.currentPage || page,
          per_page: meta.per_page || meta.perPage || per_page,
          total: meta.total ?? 0,
          last_page:
            meta.last_page || meta.lastPage || Math.max(1, Math.ceil((meta.total || 0) / (meta.per_page || per_page))),
        };

        return { items, pagination };
      }
      throw new Error(response?.message || "Failed to fetch orders");
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message || "An error occurred while fetching orders");
    }
  }
);

const preorders = createSlice({
  name: "preOrders",
  initialState: {
    items: [],
    loading: false,
    pagination: { page: 1, per_page: 4, total: 0, last_page: 1 },
    error: null,
  },
  reducers: {
    setOrders: (state, action) => {
      state.items = action.payload || [];
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setPerPage: (state, action) => {
      state.pagination.per_page = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...(action.payload || {}) };
    },
    resetPreOrders: (state) => {
      state.items = [];
      state.loading = false;
      state.pagination = { page: 1, per_page: 4, total: 0, last_page: 1 };
      state.error = null;
    },
    // upsert a single pre-order into the list (update if exists, otherwise prepend)
    upsertPreOrder: (state, action) => {
      const o = action.payload || {};
      const targetId = Number(o.id || o.order_id || o.orderId || o.pre_order?.order_id);
      if (!targetId) return;

      const idx = state.items.findIndex(
        (it) => Number(it.id || it.order_id || it.orderId || it.pre_order?.order_id) === targetId
      );

      if (idx !== -1) {
        state.items[idx] = { ...state.items[idx], ...o, id: targetId };
      } else {
        state.items.unshift({ ...o, id: targetId });
        if (state.pagination) {
          state.pagination.total = (state.pagination.total || 0) + 1;
        }
      }
    },
    removePreOrder: (state, action) => {
      const targetId = Number(action.payload);
      if (!targetId) return;

      state.items = state.items.filter(
        (it) => Number(it.id || it.order_id || it.orderId || it.pre_order?.order_id) !== targetId
      );
      if (state.pagination && state.pagination.total > 0) {
        state.pagination.total -= 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPreOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPreOrders.fulfilled, (state, action) => {
        state.items = action.payload.items || [];
        state.pagination = { ...state.pagination, ...(action.payload.pagination || {}) };
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchPreOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || "Failed to fetch orders";
      });
  },
});

export const {
  setOrders,
  setPage,
  setPerPage,
  setPagination,
  resetPreOrders,
  upsertPreOrder,
  removePreOrder,
} = preorders.actions;

export default preorders.reducer;
